export function getArgValue(argv: string[], key: string): string | undefined {
  const index = argv.indexOf(key);
  if (index === -1 || index >= argv.length - 1) return undefined;
  return argv[index + 1];
}

export function hasFlag(argv: string[], ...flags: string[]): boolean {
  return flags.some((flag) => argv.includes(flag));
}

export function buildQueryParams(tag?: string, namePattern?: string): string {
  const params = new URLSearchParams();
  if (tag) params.set('tag', tag);
  if (namePattern) params.set('namePattern', namePattern);
  return params.toString();
}

export function extractWorkflowName(message: string | undefined, workflowId: string): string {
  if (!message) return workflowId;
  const match = message.match(/^Flow "(.+?)" /);
  return match ? match[1] : workflowId;
}

export type FailedTest = { name: string; testRunId: string };

export function formatFailedTests(
  failedTests: FailedTest[],
  appUrl: string | undefined
): string | null {
  if (failedTests.length === 0 || !appUrl) return null;

  const lines = ['', 'Failed tests:'];
  for (const t of failedTests) {
    lines.push(`  \u2717 ${t.name}`);
    lines.push(`    ${appUrl}/runs/tests/${t.testRunId}`);
  }
  return lines.join('\n');
}

export function formatSummary(stats: {
  totalWorkflows: number;
  successfulWorkflows: number;
  failedWorkflows: number;
  completedWorkflows: number;
}): { message: string; exitCode: number } {
  const { totalWorkflows, successfulWorkflows, failedWorkflows, completedWorkflows } = stats;

  if (totalWorkflows === 0) {
    return { message: 'No workflows found matching the filter criteria.', exitCode: 0 };
  }

  const passRate = Math.round((successfulWorkflows / totalWorkflows) * 100);
  const waitingWorkflows = totalWorkflows - completedWorkflows;

  let message: string;
  let exitCode: number;

  if (failedWorkflows > 0) {
    message = `FAILED: ${failedWorkflows} of ${totalWorkflows} workflows failed (${passRate}% pass rate)`;
    exitCode = 1;
  } else {
    message = `PASSED: ${successfulWorkflows} of ${totalWorkflows} workflows passed`;
    exitCode = 0;
  }

  if (waitingWorkflows > 0) {
    message += `\nNote: ${waitingWorkflows} workflow(s) are still waiting (scheduled for later)`;
  }

  return { message, exitCode };
}

export function formatExternalRunSummary(stats: {
  total: number;
  passed: number;
  failed: number;
  flaky: number;
  skipped: number;
  resultsTruncated: boolean;
}): { message: string; exitCode: number } {
  const { total, passed, failed, flaky, skipped, resultsTruncated } = stats;

  if (resultsTruncated) {
    return {
      message: `FAILED: external run ended with incomplete results (${total} collected)`,
      exitCode: 1,
    };
  }
  if (total === 0) {
    return { message: 'FAILED: external run produced no test results', exitCode: 1 };
  }
  if (failed > 0) {
    return { message: `FAILED: ${failed} of ${total} external tests failed`, exitCode: 1 };
  }
  return {
    message: `PASSED: ${passed} passed, ${flaky} flaky, ${skipped} skipped`,
    exitCode: 0,
  };
}

export function formatStreamReadFailure(error: unknown): string {
  const detail = error instanceof Error && error.message.trim() ? ` (${error.message})` : '';
  return [
    `Test result stream ended unexpectedly${detail}.`,
    'The test suite may still be running in Canary.',
    'Re-run the command or check the test suite in the app for final status.',
  ].join(' ');
}

export function formatStreamClosedBeforeCompletion(): string {
  return [
    'Test result stream ended before the suite completed.',
    'The test suite may still be running in Canary.',
    'Re-run the command or check the test suite in the app for final status.',
  ].join(' ');
}
